import {
  Box,
  Checkbox,
  Flex,
  Spinner,
  Table,
  TableCaption,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from 'front-end/src/components'
import {
  OrderDescriptor,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React, { FC, useEffect } from 'react'
import {
  Column,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table-7'
import { brandColors } from 'shared/basicPalette'

import { TableProps } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { Trans } from '@lingui/macro'

import { IconArrowDown } from '../icon/IconArrowDown'
import { IconArrowUp } from '../icon/IconArrowUp'
import { Pagination } from './TableExtendedPagination'
import { fontWeights } from 'front-end/src/components/chakraThemeExtension'

interface ITableExtended {
  rowSelectable?: boolean
  loading?: boolean
  columns: Array<Column>
  data: Array<any>
  dataCount: number

  page?: number
  onPageChange?: (page: number) => any

  order?: OrderDescriptor
  onOrderChange?: (OrderDescriptor) => any

  header?: JSX.Element
  noTableHead?: boolean

  onRowClick?: (any) => any

  showSearchFilter?: boolean
  extendFilter?: JSX.Element
  tableDescription?: string | JSX.Element
  placeholder?: string
  paginationContentBefore?: JSX.Element
  paginationContentAfter?: JSX.Element
}

const IndeterminateCheckbox = ({
  indeterminate,
  checked,
  onChange,
  onClick,
  ...rest
}) => (
  <Checkbox
    isChecked={checked}
    isIndeterminate={indeterminate}
    {...rest}
    // Force these
    onClick={(ev) => {
      ev.stopPropagation()
      onClick(ev)
    }}
    onChange={(ev) => {
      ev.stopPropagation()
      onChange(ev)
    }}
  />
)

const ALIGN_CONFIG = {
  center: 'center',
  left: 'flex-start',
  right: 'flex-end'
}

const getFlexAlign = (align) => ALIGN_CONFIG[align]

export const DEFAULT_PAGE_LIMIT = 20

export const TableExtended: FC<
  Omit<TableProps, 'order'> & ITableExtended & { dataTest?: string }
> = (props) => {
  const {
    rowSelectable,
    loading,
    columns,
    data,
    dataCount,
    page,
    onPageChange,
    order,
    onOrderChange,
    header,
    noTableHead = false,
    paginationContentBefore,
    paginationContentAfter,
    onRowClick,
    dataTest,
    ...restChakraProps
  } = props

  const table = useTable(
    {
      columns,
      data,
      manualSortBy: true,
      disableSortRemove: true,
      initialState: {
        pageSize: DEFAULT_PAGE_LIMIT,
        pageIndex: 0,
        sortBy: [
          { id: order?.name, desc: order?.sort === SqlOrderDirectionEnum.DESC }
        ]
      }
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    (hooks) => {
      rowSelectable &&
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection',
            width: '45px',
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <Flex grow={1} justifyContent="center">
                <IndeterminateCheckbox
                  {...getToggleAllPageRowsSelectedProps()}
                />
              </Flex>
            ),
            Cell: ({ row }) => (
              <Flex grow={1} justifyContent="center">
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </Flex>
            )
          },
          ...columns
        ])
    }
  )

  useEffect(() => {
    const sort = table.state.sortBy
    onOrderChange &&
      sort.length &&
      onOrderChange({
        name: sort[0].id,
        sort: sort[0].desc
          ? SqlOrderDirectionEnum.DESC
          : SqlOrderDirectionEnum.ASC
      })
  }, [table.state.sortBy])

  const pageCount = Math.ceil(dataCount / DEFAULT_PAGE_LIMIT)

  return (
    <Table data-test={dataTest} {...table.getTableProps()} {...restChakraProps}>
      <Thead minH="32px">
        {header && (
          <Flex
            width="100%"
            p="16px"
            borderBottom={`1px solid ${brandColors.grey[50]}`}
          >
            {header}
          </Flex>
        )}
        {!noTableHead &&
          table.headerGroups.map((headerGroup, index) => (
            <Tr
              key={index}
              {...headerGroup.getHeaderGroupProps()}
              borderBottom="0px"
            >
              {headerGroup.headers.map((column, index) => (
                <Th
                  key={index}
                  tabIndex={0}
                  color={column.isSorted ? brandColors.grey[900] : undefined}
                  fontWeight={column.isSorted ? fontWeights.bold : undefined}
                  borderBottom="0px"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  width={column.width}
                  textAlign={column.align}
                  _hover={column.disableSortBy && { color: undefined }}
                >
                  {column.render('Header')}
                  {column.id !== 'selection' && (
                    <Box as="span" ml="6px" verticalAlign="middle">
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <IconArrowDown boxSize="12px" />
                        ) : (
                          <IconArrowUp boxSize="12px" />
                        )
                      ) : (
                        ''
                      )}
                    </Box>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
      </Thead>
      <Tbody {...table.getTableBodyProps()}>
        {loading ? (
          <Tr>
            <Td colSpan={table.allColumns.length} textAlign="center">
              <Spinner size="lg" m="36px" />
            </Td>
          </Tr>
        ) : (
          <>
            {data.length === 0 ? (
              <Tr>
                <Td
                  colSpan={table.allColumns.length}
                  textAlign="center"
                  color={brandColors.grey[400]}
                  p="96px 0"
                >
                  <Trans>Nothing to show</Trans>
                </Td>
              </Tr>
            ) : (
              table.page.map((row, index) => {
                table.prepareRow(row)
                return (
                  <Tr
                    key={index}
                    _hover={{
                      bg: brandColors.greyTransparent[2]
                    }}
                    tabIndex={0}
                    css={css`
                      // Show/hide menu button or element with className="hide-and-show-on-hover"
                      button.chakra-menu__menu-button,
                      .hide-and-show-on-hover {
                        opacity: 0;
                        &[aria-expanded='true'] {
                          opacity: 1;
                        }
                        &:focus {
                          opacity: 1;
                        }
                      }

                      &:hover button.chakra-menu__menu-button,
                      &:hover .hide-and-show-on-hover {
                        opacity: 1;
                      }
                    `}
                    onKeyPress={(ev) => {
                      // Accessibility - enter triggers click
                      if (ev.key === 'Enter') {
                        onRowClick && onRowClick(row)
                      }
                    }}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell, index) => {
                      const clickable =
                        onRowClick &&
                        cell.column.id !== 'selection' &&
                        cell.column.id !== 'menu'
                      return (
                        <Td
                          key={index}
                          borderBottom="0px"
                          bg="transparent"
                          {...cell.getCellProps()}
                          width={cell.column.width}
                          // @ts-ignore
                          onClick={() => clickable && onRowClick(row)}
                          _hover={{
                            cursor: clickable ? 'pointer' : undefined
                          }}
                        >
                          <Flex
                            h="100%"
                            justifyContent={getFlexAlign(cell.column.align)}
                          >
                            {cell.render('Cell')}
                          </Flex>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })
            )}
          </>
        )}
      </Tbody>
      <TableCaption mt="0">
        <Flex grow={1}>
          <Flex justifyContent="start" grow={1}>
            {paginationContentBefore}
          </Flex>
          <Flex justifyContent="center">
            {!loading && onPageChange && pageCount > 1 && (
              <Pagination
                pageCurrent={page || 0}
                pageCount={pageCount}
                onPageChange={onPageChange}
              />
            )}
          </Flex>
          <Flex justifyContent="start" grow={1}>
            {paginationContentAfter}
          </Flex>
        </Flex>
      </TableCaption>
    </Table>
  )
}
